import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { t } from "i18next";

import {
  getCitiesByState,
  getCountries,
  getStatesByCountry,
  resetCityField,
  resetStateField,
} from "../../redux/slice/form-field/formFieldSlice";
import { getSearchProviders } from "../../redux/slice/provider-search/searchProvidersSlice";
import { setLocalStorageItem } from "../../service/utils";

import DropdownField from "./form-fields/DropdownField";
import InputField from "./form-fields/InputField";
import Button from "./form-fields/Button";

const NetworkSearchForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { countriesData, statesData, citiesData } = useSelector(
    (state) => state.formField
  );

  useEffect(() => {
    dispatch(getCountries());
  }, [dispatch]);

  const handleCountryChange = (event, setValues) => {
    const selectedCountryId = event.target.value;

    if (selectedCountryId) {
      dispatch(getStatesByCountry(selectedCountryId));
    } else {
      dispatch(resetStateField());
      dispatch(resetCityField());
    }

    setValues({
      countrySearch: selectedCountryId,
      stateSearch: "",
      citySearch: "",
    });
  };

  const handleStateChange = (event, values, setValues) => {
    const selectedStateId = event.target.value;

    if (selectedStateId) {
      dispatch(
        getCitiesByState({
          country: values.countrySearch,
          state: selectedStateId,
        })
      );
    } else {
      dispatch(resetCityField());
    }

    setValues({
      ...values,
      stateSearch: selectedStateId,
      citySearch: "",
    });
  };

  return (
    <div>
      <h4>{t("provider-location-criteria-label")}</h4>
      <p>{t("all-search-criteria-below-are-optional")}</p>

      <Formik
        initialValues={{
          countrySearch: "",
          stateSearch: "",
          citySearch: "",
          zipSearch: "",
          organizationSearch: "",
        }}
        onSubmit={(values) => {
          const payload = {
            delta: 10,
            pageNumber: 1,
            sortDirection: 1,
            isDownloadPdf: false,
            countrySearch: values.countrySearch,
            stateSearch: values.stateSearch,
            citySearch: values.citySearch,
            zipSearch: values.zipSearch,
            addressSearch: null,
            specialitySearch: "",
            organizationSearch: values.organizationSearch,
            isSpecialitySearch: false,
          };

          dispatch(getSearchProviders(payload));
          setLocalStorageItem("searchValues", payload);
          navigate("/providerSearch");
        }}
        enableReinitialize
      >
        {({
          values,
          handleSubmit,
          handleChange,
          handleReset,
          resetForm,
          setValues,
        }) => (
          <form onSubmit={handleSubmit} onReset={handleReset}>
            {/* Country */}
            <div className="form-group">
              <label htmlFor="countrySearch" className="col-md-4">
                {t("country")}
              </label>
              <div className="col-md-8 form-field">
                <DropdownField
                  id="countrySearch"
                  name="countrySearch"
                  placeHolder={t("country-placeholder")}
                  options={countriesData}
                  value={values.countrySearch}
                  onChange={(event) => {
                    handleChange(event);
                    handleCountryChange(event, setValues);
                  }}
                />
              </div>
            </div>

            {/* State */}
            <div className="form-group">
              <label htmlFor="stateSearch" className="col-md-4">
                {t("state-province")}
              </label>
              <div className="col-md-8 form-field">
                <DropdownField
                  id="stateSearch"
                  name="stateSearch"
                  placeHolder={t("state-province-placeholder")}
                  options={statesData}
                  value={values.stateSearch}
                  onChange={(event) => {
                    handleChange(event);
                    handleStateChange(event, values, setValues);
                  }}
                />
              </div>
            </div>

            {/* City & Zip */}
            <div className="form-group">
              <label htmlFor="citySearch" className="col-md-4">
                {t("city")}
              </label>
              <div className="col-md-3 form-field">
                <DropdownField
                  id="citySearch"
                  name="citySearch"
                  placeHolder={t("city-placeholder")}
                  options={citiesData}
                  value={values.citySearch}
                  onChange={handleChange}
                />
              </div>

              <label htmlFor="zipSearch" className="col-md-2 zip-label">
                {t("zip-postal-code")}
              </label>
              <div className="col-md-3 form-field">
                <InputField
                  id="zipSearch"
                  name="zipSearch"
                  placeholder={t("zip-postal-code-placeholder")}
                  type="text"
                  value={values.zipSearch}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Provider Name */}
            <h4>{t("provider-name")}</h4>

            <div className="form-group">
              <label htmlFor="organizationSearch" className="col-md-4">
                {t("organization-name")}
              </label>
              <div className="col-md-8 form-field">
                <InputField
                  id="organizationSearch"
                  name="organizationSearch"
                  placeholder={t("organization-name-placeholder")}
                  type="text"
                  value={values.organizationSearch}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="buttons btn-icon">
              <Button
                isIcon
                icon="icon-search"
                buttonText={t("search-button")}
                buttonType="submit"
                className="btn-orange-icon"
              />

              <Button
                isIcon
                icon="icon-refresh"
                buttonText={t("clear-button")}
                buttonType="button"
                className="btn-orange-icon"
                onClick={() => {
                  resetForm();
                  dispatch(resetStateField());
                  dispatch(resetCityField());
                }}
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default NetworkSearchForm;
